import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Using the same base URL pattern as other APIs
// The actual endpoint will be /stock/vendors which routes to the correct Lambda
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log('Vendor API: Token obtained successfully');
      return idToken;
    }
    
    console.warn('Vendor API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Vendor API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT', 'DELETE').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Vendor API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const responseText = await response.text();
      let errorData = {};
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        if (responseText) {
          errorData = JSON.parse(responseText);
          errorMessage = errorData.body?.error || 
                        errorData.error || 
                        errorData.message || 
                        errorMessage;
        }
      } catch (parseError) {
        console.warn('Could not parse error response as JSON');
        errorData = { message: responseText || errorMessage };
      }
      
      console.error(
        `Vendor API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          errorData,
          requestBody: body ? JSON.parse(JSON.stringify(body)) : null,
        }
      );

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      // Log API error to logging service
      loggingService.logError(
        'api_error_vendor_onboarding',
        `Vendor Onboarding API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[vendorApi] Failed to log error:', logError);
      });

      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      // Log authentication error to logging service
      loggingService.logError(
        'api_error_vendor_onboarding',
        `Vendor Onboarding API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[vendorApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.error(`Vendor API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;

    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_vendor_onboarding',
      `Vendor Onboarding API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[vendorApi] Failed to log error:', logError);
    });

    throw error;
  }
};

/**
 * API utility functions for Vendor Management.
 */
export const vendorApi = {
  /**
   * Creates a new vendor.
   * POST /stock/vendors
   * @param {Object} vendorData - Vendor data according to API spec
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createVendor: async (vendorData) => {
    try {
      const endpoint = '/stock/vendors';
      
      console.log('Vendor API: Creating vendor');
      console.log('Vendor API: Request payload:', JSON.stringify(vendorData, null, 2));
      console.log('Vendor API: Endpoint:', `${API_BASE_URL}${endpoint}`);
      
      const response = await makeApiRequest(endpoint, 'POST', vendorData);
      
      let responseData;
      try {
        responseData = await response.json();
        console.log('Vendor API: Create vendor response:', responseData);
      } catch (jsonError) {
        console.error('Vendor API: Failed to parse response as JSON:', jsonError);
        const textResponse = await response.text();
        console.error('Vendor API: Raw response:', textResponse);
        throw new Error('Invalid response format from server');
      }

      return {
        success: true,
        data: responseData,
        vendor: responseData.vendor,
      };
    } catch (error) {
      console.error('Vendor API: Error in createVendor:', error);
      console.error('Vendor API: Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status === 502) {
        return {
          success: false,
          error: 'Bad Gateway',
          message: 'The vendor API endpoint is currently unavailable (502 Bad Gateway). This indicates a backend Lambda function error. Please check:\n\n1. Lambda function "innoworx-vendor-create" is deployed and configured correctly\n2. Lambda function has sufficient timeout and memory\n3. API Gateway is correctly routing to the Lambda\n4. Check CloudWatch logs for the Lambda function\n\nPayload sent was valid. This is a backend infrastructure issue.',
          technicalDetails: {
            endpoint: error.endpoint,
            status: error.status,
            payload: vendorData,
          },
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || error.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create vendor. Please check your connection and try again.',
      };
    }
  },

  /**
   * Lists vendors with optional filters.
   * GET /stock/vendors
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.last_key] - Pagination token
   * @param {string} [params.name_contains] - Case-insensitive substring filter on vendor_name
   * @param {string} [params.status] - Status filter (active, inactive, etc.)
   * @returns {Promise<Object>} Object containing success status, vendors array, and pagination info
   */
  listVendors: async ({ limit, last_key, name_contains, status } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (last_key !== undefined && last_key !== null) {
        queryParams.append('last_key', last_key);
      }
      if (name_contains !== undefined && name_contains !== null) {
        queryParams.append('name_contains', name_contains);
      }
      if (status !== undefined && status !== null) {
        queryParams.append('status', status);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/vendors${queryString ? `?${queryString}` : ''}`;

      console.log(`Vendor API: Listing vendors: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Vendor API: List vendors response:', responseData);

      return {
        success: true,
        data: responseData,
        vendors: responseData.vendors || [],
        count: responseData.count || 0,
        last_key: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Vendor API: Error in listVendors:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          vendors: [],
          count: 0,
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          vendors: [],
          count: 0,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to list vendors',
        vendors: [],
        count: 0,
      };
    }
  },

  /**
   * Gets a single vendor by ID.
   * GET /stock/vendors/{vendor_id}
   * @param {string} vendorId - Vendor identifier
   * @returns {Promise<Object>} Object containing success status and vendor data
   */
  getVendor: async (vendorId) => {
    try {
      if (!vendorId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Vendor ID is required',
        };
      }

      const endpoint = `/stock/vendors/${vendorId}`;
      
      console.log(`Vendor API: Getting vendor: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Vendor API: Get vendor response:', responseData);

      return {
        success: true,
        data: responseData,
        vendor: responseData.vendor,
      };
    } catch (error) {
      console.error('Vendor API: Error in getVendor:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to get vendor',
      };
    }
  },

  /**
   * Updates a vendor (partial updates supported).
   * PUT /stock/vendors/{vendor_id}
   * @param {string} vendorId - Vendor identifier
   * @param {Object} vendorData - Partial vendor data to update
   * @returns {Promise<Object>} Object containing success status and updated vendor data
   */
  updateVendor: async (vendorId, vendorData) => {
    try {
      if (!vendorId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Vendor ID is required',
        };
      }

      const endpoint = `/stock/vendors/${vendorId}`;
      
      console.log(`Vendor API: Updating vendor: ${endpoint}`, vendorData);
      const response = await makeApiRequest(endpoint, 'PUT', vendorData);
      const responseData = await response.json();

      console.log('Vendor API: Update vendor response:', responseData);

      return {
        success: true,
        data: responseData,
        vendor: responseData.vendor,
      };
    } catch (error) {
      console.error('Vendor API: Error in updateVendor:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update vendor',
      };
    }
  },

  /**
   * Deactivates a vendor (soft delete).
   * DELETE /stock/vendors/{vendor_id}
   * @param {string} vendorId - Vendor identifier
   * @returns {Promise<Object>} Object containing success status and deactivated vendor data
   */
  deactivateVendor: async (vendorId) => {
    try {
      if (!vendorId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Vendor ID is required',
        };
      }

      const endpoint = `/stock/vendors/${vendorId}`;
      
      console.log(`Vendor API: Deactivating vendor: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'DELETE');
      const responseData = await response.json();

      console.log('Vendor API: Deactivate vendor response:', responseData);

      return {
        success: true,
        data: responseData,
        vendor: responseData.vendor,
      };
    } catch (error) {
      console.error('Vendor API: Error in deactivateVendor:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to deactivate vendor',
      };
    }
  },

  /**
   * Gets a presigned URL for uploading vendor logo.
   * POST /stock/vendors/{vendor_id}/logo/presign
   * @param {string} vendorId - Vendor identifier (must exist)
   * @param {string} [contentType] - MIME type (default: "image/jpeg")
   * @returns {Promise<Object>} Object containing success status and presigned URL data
   */
  presignLogoUpload: async (vendorId, contentType = 'image/jpeg') => {
    try {
      if (!vendorId) {
        return {
          success: false,
          error: 'Validation error',
          message: 'Vendor ID is required',
        };
      }

      const queryParams = new URLSearchParams();
      if (contentType) {
        queryParams.append('content_type', contentType);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/vendors/${vendorId}/logo/presign${queryString ? `?${queryString}` : ''}`;
      
      console.log(`Vendor API: Getting presigned URL for logo: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'POST');
      const responseData = await response.json();

      console.log('Vendor API: Presign logo response:', responseData);

      return {
        success: true,
        data: responseData,
        presigned_post: responseData.presigned_post,
        object_key: responseData.object_key,
        content_type: responseData.content_type,
      };
    } catch (error) {
      console.error('Vendor API: Error in presignLogoUpload:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to get presigned URL for logo',
      };
    }
  },
};

